import * as React from 'react';

import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {
    ArchiveIcon,
    EyeIcon,
    GlobeIcon,
    HtmlLinkIcon,
    TrashIcon,
    LockedIcon,
    UnlockIcon,
} from 'shells/icon';
import {ThumbnailButtonCard} from 'shells/thumbnail/thumbnail-button-card/thumbnail-button-card';

import type {MeetingStubFragment, MeetingDeleteInput} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useDeleteMeeting, useUndeleteMeeting, useHideMeeting} from '../graphql/hooks';
import {getStatusBadge, getAdditionalStatusBadges, getManagedMeetingBadge} from '../helpers';
import {ShareMeetingModalComponent} from '../share-meeting/share-meeting-modal-component';

import {MeetingBookingInfo} from './meeting-booking-info';
import {MeetingReviewCardBottom} from './meeting-review-card-bottom';

type Props = {
    meeting: MeetingStubFragment;
    activeFilters: FilterObject[];
};

export function MeetingReviewCard(props: Props) {
    const [isViewingShareModal, setIsViewingShareModal] = React.useState<boolean>(false);

    const hostFilter = safelyGetFilterByKey(props.activeFilters, 'host');
    const hostIds = hostFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(hostFilter)
        : undefined;
    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const deleteMeeting = useDeleteMeeting({entityIds: hostIds, archived: isShowingArchived});
    const handleDelete = ({id}: MeetingDeleteInput) => {
        deleteMeeting({id});
    };

    const undeleteMeeting = useUndeleteMeeting({entityIds: hostIds, archived: isShowingArchived});
    const handleUndelete = (meetingId: string) => {
        undeleteMeeting({meetingId});
    };

    const updateMeeting = useHideMeeting();
    const handleHideMeeting = (meetingId: string, isHidden: boolean) => {
        updateMeeting({id: meetingId, isHidden});
    };

    const openMeeting = () => appHistory.push(`/sales/scheduler/${props.meeting.id}`);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(props.meeting.publicMeetingLink);
    };

    // @ts-expect-error
    let hiddenMenuOption = [];
    if (props.meeting.isShowable) {
        hiddenMenuOption = [
            {
                key: 'make-public',
                label: 'Make public',
                icon: <UnlockIcon size={15} />,
                handleSelect: () => {
                    handleHideMeeting(props.meeting.id, false);
                },
                tooltip: 'Making this public adds it back to your meeting page.',
            },
        ];
    } else if (props.meeting.isHideable) {
        hiddenMenuOption = [
            {
                key: 'hide-from-page',
                label: 'Hide from page',
                icon: <LockedIcon size={15} />,
                handleSelect: () => {
                    handleHideMeeting(props.meeting.id, true);
                },
                tooltip: 'Hiding this meeting removes it from your public meeting page.',
            },
        ];
    }

    let actionMenuOptions;
    if (isShowingArchived) {
        if (!props.meeting.isManagedMeeting) {
            actionMenuOptions = [
                {
                    key: 'restore',
                    label: 'Restore',
                    icon: <ArchiveIcon size={16} />,
                    handleSelect: () => handleUndelete(props.meeting.id),
                },
            ];
        }
    } else {
        actionMenuOptions = [
            {
                key: 'view',
                label: 'View',
                icon: <EyeIcon size={18} />,
                handleSelect: openMeeting,
            },
            {
                key: 'share',
                label: 'Share',
                icon: <GlobeIcon size={18} />,
                handleSelect: () => setIsViewingShareModal(true),
            },
            {
                key: 'copy-link',
                label: 'Copy link',
                icon: <HtmlLinkIcon size={15} />,
                handleSelect: handleCopyLink,
                tooltipTextOnClose: 'Copied!',
            },
            // @ts-expect-error
            ...hiddenMenuOption,
        ];

        if (!props.meeting.isManagedMeeting) {
            actionMenuOptions.push({
                key: 'archive',
                label: 'Archive',
                icon: <TrashIcon size={16} />,
                variant: 'destructive',
                handleSelect: () => handleDelete({id: props.meeting.id}),
            });
        }
    }

    return (
        <>
            <ThumbnailButtonCard
                title={props.meeting.name}
                secondaryInfo={
                    <MeetingBookingInfo
                        numBookings={props.meeting.bookings.length}
                        lastBookedTime={props.meeting.lastBookedTime}
                    />
                }
                tertiaryInfo={<MeetingReviewCardBottom meeting={props.meeting} />}
                onClick={openMeeting}
                options={actionMenuOptions}
                statusBadge={getStatusBadge(props.meeting)}
                additionalStatusBadges={getAdditionalStatusBadges(props.meeting)}
                rightAlignedStatusBadge={getManagedMeetingBadge(props.meeting)}
            />
            {isViewingShareModal && (
                <ShareMeetingModalComponent
                    meetingIsActive={props.meeting.isActive}
                    publicMeetingLink={props.meeting.publicMeetingLink}
                    embedSnippet={props.meeting.embedSnippet}
                    isOpen={true}
                    onClose={() => setIsViewingShareModal(false)}
                />
            )}
        </>
    );
}
